import { useMemo } from "react";
import {
  useAvailablePaymentRailsQuery,
  useAvailablePaymentRailsLazyQuery,
} from "../../../graphql/supergraphHooks";
import {
  type AvailablePaymentRailsQuery,
  type PaymentRailAvailabilityObjectType as PaymentRailAvailabilityType,
  type PositionCheckSupportObjectType as PositionCheckSupportType,
} from "../../../graphql/supergraphTypes";

/** Per-position country restrictions for the Check rail. `allowedCountryIds === null` means unrestricted. */
export type PositionCheckSupport = PositionCheckSupportType;

export type PaymentRailAvailability = PaymentRailAvailabilityType;

export type AvailablePaymentRailsResponse = AvailablePaymentRailsQuery["availablePaymentRails"];

export type UseAvailablePaymentRailsOptions = {
  positionId?: string;
  accountIds?: string[];
  paymentPrefIds?: string[] | null;
  countryId?: string;
  isLpPortal?: boolean;
  /** Scopes the lookup to a subscription's offering-derived intl rails. */
  subscriptionId?: string;
  /** Skip the query until the caller is ready (e.g. account-level flow waiting for country). */
  skip?: boolean;
};

/**
 * React hook for fetching available payment rails for a position, account, or LP Portal context.
 *
 * returns the raw response plus Apollo loading/error state and a refetch function
 * for re-querying after the user picks a bank country.
 */
export const useAvailablePaymentRails = ({
  positionId,
  accountIds,
  paymentPrefIds,
  countryId,
  isLpPortal,
  subscriptionId,
  skip,
}: UseAvailablePaymentRailsOptions) => {
  const variables = useMemo(
    () => ({ positionId, accountIds, paymentPrefIds, countryId, isLpPortal, subscriptionId }),
    [positionId, accountIds, paymentPrefIds, countryId, isLpPortal, subscriptionId],
  );
  const { data, loading, error, refetch } = useAvailablePaymentRailsQuery({
    variables,
    skip,
  });

  return {
    response: data?.availablePaymentRails,
    loading,
    error,
    refetch,
  };
};

/**
 * Lazy variant used when the caller wants to defer the network call until an explicit user action
 * (e.g. country selection on account-level / LP Portal flows).
 */
export const useAvailablePaymentRailsLazy = useAvailablePaymentRailsLazyQuery;
